import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const initializedRef = useRef(false);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Initialize from localStorage only once
    return localStorage.getItem("selectedLanguage") || "en";
  });

  useEffect(() => {
    // Set initial language only on first mount to prevent infinite loop
    if (!initializedRef.current) {
      initializedRef.current = true;
      const savedLanguage = localStorage.getItem("selectedLanguage");
      if (savedLanguage && i18n.language !== savedLanguage) {
        i18n.changeLanguage(savedLanguage);
      }
    }
  }, [i18n]);

  const changeLanguage = React.useCallback(
    (languageCode) => {
      i18n.changeLanguage(languageCode);
      localStorage.setItem("selectedLanguage", languageCode);
      setCurrentLanguage(languageCode);
    },
    [i18n]
  );

  const getLanguageName = React.useCallback((code) => {
    const languages = {
      en: "English",
      si: "සිංහල",
      ta: "தமிழ්",
    };
    return languages[code] || "English";
  }, []);

  const value = React.useMemo(
    () => ({
      currentLanguage,
      changeLanguage,
      getLanguageName,
      availableLanguages: [
        { code: "en", name: "English", nativeName: "English" },
        { code: "si", name: "Sinhala", nativeName: "සිංහල" },
        { code: "ta", name: "Tamil", nativeName: "தமிழ்" },
      ],
    }),
    [currentLanguage, changeLanguage, getLanguageName]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
